import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useUpdateAvailable } from "@/hooks/useUpdateAvailable";
import { performFullUpdate } from "@/lib/versionChecker";

/**
 * Floating pill shown at the top of the app whenever a newer build has been
 * detected. Clicking it fully purges browser caches + service workers then
 * hard-reloads. Fixed positioning ensures visibility across every route
 * (including `/chat`, which hides the header).
 */
export default function UpdateAvailableButton() {
  const { t } = useTranslation();
  const available = useUpdateAvailable();
  const [loading, setLoading] = useState(false);

  if (!available) return null;

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await performFullUpdate();
    } catch {
      setLoading(false);
    }
  };

  const label = loading
    ? t("update.available.loading", "Mise à jour…")
    : t("update.available.button", "Mettre à jour Thiia");

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[200] pointer-events-none"
      style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={handleClick}
            disabled={loading}
            size="sm"
            className="pointer-events-auto h-9 px-3 rounded-full shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
            aria-label={label}
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            <span className="text-xs font-medium">{label}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {t("update.available.tooltip", "Une nouvelle version de Thiia est disponible")}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
